import React, {useContext, useEffect, useRef, useState} from 'react'
import {Icon} from 'semantic-ui-react'
import {AuthContext} from '../../Auth'
import dbApp from '../../firebase'
import './AdminAssistant.css'

const initialMessages = [{
  role: 'assistant',
  content: 'Hola. Puedo consultar, agregar y modificar Pendientes no completados, Asignados, Asistencia, Actividades y Zonas. Antes de guardar un cambio te pediré confirmación.',
}]

const errorMessage = error => {
  const code = String(error?.code || '')
  if (code.includes('resource-exhausted')) return 'Alcanzaste el límite diario del asistente.'
  if (code.includes('permission-denied')) return 'Tu sesión no tiene permisos de administrador.'
  if (code.includes('unauthenticated')) return 'Tu sesión expiró. Inicia sesión nuevamente.'
  return 'No pude contactar al asistente. Intenta nuevamente en unos momentos.'
}

const AdminAssistant = () => {
  const {currentUser} = useContext(AuthContext)
  const [open, setOpen] = useState(false)
  const [messages, setMessages] = useState(initialMessages)
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)
  const [pendingAction, setPendingAction] = useState(null)
  const endRef = useRef(null)
  const isAdmin = currentUser?.rol === 'admin' || currentUser?.email === 'admin@admin.com'

  useEffect(() => {
    if (open) endRef.current?.scrollIntoView({behavior: 'smooth'})
  }, [messages, loading, open])

  if (!isAdmin) return null

  const sendMessage = async event => {
    event.preventDefault()
    const content = input.trim()
    if (!content || loading || pendingAction) return
    const nextMessages = [...messages, {role: 'user', content}].slice(-20)
    setMessages(nextMessages)
    setInput('')
    setLoading(true)
    try {
      const authUser = dbApp.auth().currentUser
      if (!authUser) throw new Error('Tu sesión expiró. Inicia sesión nuevamente.')
      await authUser.getIdToken(true)
      const askAssistant = dbApp.functions('us-central1').httpsCallable('adminAssistant')
      const result = await askAssistant({messages: nextMessages})
      const text = result?.data?.text || 'El asistente no devolvió una respuesta.'
      setMessages(current => [...current, {role: 'assistant', content: text}].slice(-20))
      setPendingAction(result?.data?.pendingAction || null)
    } catch (error) {
      setMessages(current => [...current, {role: 'assistant', content: errorMessage(error)}].slice(-20))
    } finally {
      setLoading(false)
    }
  }

  const resolveAction = async confirm => {
    if (!pendingAction || loading) return
    setLoading(true)
    try {
      const functionName = confirm ? 'confirmAdminAssistantAction' : 'cancelAdminAssistantAction'
      const callable = dbApp.functions('us-central1').httpsCallable(functionName)
      const result = await callable({proposalId: pendingAction.proposalId})
      const content = confirm
        ? `Listo. Se aplicaron ${result?.data?.count || pendingAction.operations.length} cambio(s).`
        : 'La propuesta fue cancelada. No se guardó ningún cambio.'
      setMessages(current => [...current, {role: 'assistant', content}].slice(-20))
      setPendingAction(null)
    } catch (error) {
      setMessages(current => [...current, {role: 'assistant', content: error?.message || 'No pude procesar la propuesta.'}].slice(-20))
    } finally {
      setLoading(false)
    }
  }

  return (
    <aside className="adminAssistant" aria-label="Asistente administrativo">
      {open && (
        <section className="adminAssistant__panel" role="dialog" aria-modal="false" aria-label="Chat administrativo">
          <header className="adminAssistant__header">
            <div><strong>Asistente Agrari</strong><small>Solo administradores</small></div>
            <button type="button" onClick={() => setOpen(false)} aria-label="Cerrar chat"><Icon name="close" /></button>
          </header>
          <div className="adminAssistant__messages" aria-live="polite">
            {messages.map((message, index) => (
              <div key={`${message.role}-${index}`} className={`adminAssistant__message adminAssistant__message--${message.role}`}>
                {message.content}
              </div>
            ))}
            {loading && <div className="adminAssistant__message adminAssistant__message--assistant">Pensando…</div>}
            {pendingAction && (
              <div className="adminAssistant__proposal">
                <strong>Confirma antes de guardar</strong>
                <p>{pendingAction.summary}</p>
                <ul>
                  {pendingAction.operations.map((operation, index) => (
                    <li key={`${operation.resource}-${operation.documentId || index}`}>
                      {operation.action === 'create' ? 'Agregar' : 'Modificar'} {operation.resource}
                    </li>
                  ))}
                </ul>
                <div className="adminAssistant__proposalActions">
                  <button type="button" className="adminAssistant__cancel" onClick={() => resolveAction(false)} disabled={loading}>Cancelar</button>
                  <button type="button" onClick={() => resolveAction(true)} disabled={loading}>Confirmar</button>
                </div>
              </div>
            )}
            <div ref={endRef} />
          </div>
          <form className="adminAssistant__form" onSubmit={sendMessage}>
            <label className="adminAssistant__srOnly" htmlFor="admin-assistant-input">Mensaje</label>
            <textarea
              id="admin-assistant-input"
              value={input}
              maxLength={4000}
              rows={2}
              placeholder="¿Qué necesitas hacer?"
              onChange={event => setInput(event.target.value)}
              onKeyDown={event => {
                if (event.key === 'Enter' && !event.shiftKey) sendMessage(event)
              }}
              disabled={loading || Boolean(pendingAction)}
            />
            <button type="submit" disabled={loading || Boolean(pendingAction) || !input.trim()} aria-label="Enviar mensaje"><Icon name="send" /></button>
          </form>
          <small className="adminAssistant__notice">Las altas y modificaciones requieren confirmación.</small>
        </section>
      )}
      <button
        className="adminAssistant__launcher"
        type="button"
        onClick={() => setOpen(value => !value)}
        aria-expanded={open}
        aria-label={open ? 'Cerrar asistente' : 'Abrir asistente'}
      >
        <Icon name="comments" /> {open ? 'Cerrar' : 'Asistente IA'}
      </button>
    </aside>
  )
}

export default AdminAssistant
